/**
 * Campos da barra de filtros da consolidação de carteira, a partir do OPTIONS
 * da rota (`client_data` no escopo de cliente, `group_data` no de grupo).
 *
 * ── A dimensão MEMBRO (`id_cliente`) ────────────────────────────────────────
 *
 * `MEMBER_FIELD_KEY` é o nome do parâmetro da API: o mesmo `id_cliente` que o
 * OPTIONS de grupo devolve em `group_by_available_values`, o mesmo `modo` de
 * `WALLET_MODO_CONFIGS` e o mesmo `group_by` que a carga do breakdown por
 * membro já manda (`GROUP_BY_MEMBER`, em `apiConsolidatorGroupWalletAnalysis`).
 * Uma chave só nos três lugares dispensa camada de tradução.
 *
 * ★ O campo de Membro é o ÚNICO condicional deste builder, e é de propósito.
 * Todas as outras dimensões nascem sempre, com `items: []` quando a fonte não
 * veio. Isso não serve aqui porque `buildPrimaryDropdownItems` monta o
 * "Agrupar por" pela PRESENÇA do campo, sem olhar `items.length` — um campo
 * sempre-presente colocaria "Membro" no dropdown da tela por CLIENTE, onde a
 * dimensão não existe.
 *
 * O gate é uma LISTA NÃO VAZIA sob a chave (`hasMemberDimension`), e não um flag
 * de escopo, porque:
 *   1. a única fonte dos itens é o payload — sem itens, o campo seria um item de
 *      menu que não filtra nada, e esse é o modo de falha caro aqui: com a forma
 *      errada o backend devolve carteira VAZIA com HTTP 200, sem erro. Por isso
 *      o gate é mais estrito que a mera presença da chave: um grupo sem membro
 *      cotizado ganharia um "Membro" morto no dropdown, já que o consumidor o
 *      monta pela presença do CAMPO;
 *   2. medido em 03/09 contra o escritório 83: o OPTIONS por CLIENTE não traz
 *      `id_cliente` (traz `setor`, que o de grupo não traz) e o de GRUPO traz.
 *      Lista com itens e escopo de grupo são a mesma coisa na prática, e o gate
 *      ainda degrada com segurança se o backend parar de emitir;
 *   3. um flag teria de ser costurado nos TRÊS produtores, incluindo o
 *      placeholder de `consolidator-wallet-filter.vue`, que não conhece escopo.
 *
 * ── Gate de perfil ─────────────────────────────────────────────────────────
 *
 * `CONSULTANT_ONLY_FIELD_KEYS` corta dimensões que o backend NÃO gateia:
 * `usecase_portfolio_consolidation_options.py` emite `tags` sem olhar
 * `is_client`, e o OPTIONS de grupo emite `id_cliente` com nome e e-mail dos
 * OUTROS membros. O corte vale para CADA produtor de
 * `walletAnalysisOptionsFields` — a rota e o placeholder.
 *
 * ── Dimensões de id ────────────────────────────────────────────────────────
 *
 * `LABELED_FIELD_KEYS` são as que listam `{id, label}`: o valor enviado é um
 * id e a tela precisa do rótulo para não exibir número cru na linha agrupada.
 * As demais dimensões já SÃO o próprio rótulo (listas de string).
 *
 * `mapIdOptions` aceita os DOIS contratos do backend: `{value, label}` (rota de
 * grupo) e o legado nomeado por dimensão (`{id_conta, conta}`,
 * `{id_cliente, nome}`). Sem rótulo cai no id — item em branco é pior que o
 * número.
 */

const STRING_GROUP_FIELDS = [
  { key: 'tipo', label: 'Tipo', source: 'tipo' },
  { key: 'subtipo', label: 'Subtipo', source: 'subtipo' },
  { key: 'classe', label: 'Classe', source: 'classe' },
  { key: 'subclasse', label: 'Subclasse', source: 'subclasse' },
  { key: 'corretora', label: 'Instituição', source: 'corretora' },
  { key: 'nome_portfolio', label: 'Portfólio', source: 'nome_portfolio' },
  { key: 'setor', label: 'Setor', source: 'setor' },
  { key: 'emissor', label: 'Emissor', source: 'emissor' },
  { key: 'tags', label: 'Tags', source: 'tags' },
]

export const ACCOUNT_FIELD_KEY = 'idConta'
export const MEMBER_FIELD_KEY = 'id_cliente'
const MEMBER_FIELD_LABEL = 'Membro'

const CONSULTANT_ONLY_FIELD_KEYS = new Set(['tags', MEMBER_FIELD_KEY])

const LABELED_FIELD_KEYS = new Set([ACCOUNT_FIELD_KEY, MEMBER_FIELD_KEY])

export const isLabeledOptionsField = (fieldKey) =>
  LABELED_FIELD_KEYS.has(fieldKey)

export const buildWalletOptionsFields = (clientData) => {
  const groups = (clientData && clientData.group_by_available_values) || {}
  const fields = STRING_GROUP_FIELDS.map(({ key, label, source }) =>
    buildStringField(key, label, groups[source])
  )
  const idFields = [buildAccountField(groups.id_conta)]
  if (hasMemberDimension(groups)) {
    idFields.push(buildMemberField(groups[MEMBER_FIELD_KEY]))
  }
  const tagsIndex = fields.findIndex((field) => field.key === 'tags')
  if (tagsIndex >= 0) {
    fields.splice(tagsIndex, 0, ...idFields)
  } else {
    fields.push(...idFields)
  }
  return fields
}

const hasMemberDimension = (groups) => {
  const items = groups[MEMBER_FIELD_KEY]
  return Array.isArray(items) && items.length > 0
}

const buildStringField = (key, label, items) => ({
  key,
  label,
  items: cleanStringList(items),
  multiple: true,
})

const buildAccountField = (items) => ({
  key: ACCOUNT_FIELD_KEY,
  label: 'Conta',
  items: mapIdOptions(items, { valueKey: 'id_conta', labelKey: 'conta' }),
  multiple: true,
  itemText: 'label',
  itemValue: 'id',
})

const buildMemberField = (items) => ({
  key: MEMBER_FIELD_KEY,
  label: MEMBER_FIELD_LABEL,
  items: mapIdOptions(items, { valueKey: 'id_cliente', labelKey: 'nome' }),
  multiple: true,
  itemText: 'label',
  itemValue: 'id',
})

const cleanStringList = (items) => {
  if (!Array.isArray(items)) return []
  return items.filter((item) => typeof item === 'string' && item.length > 0)
}

const mapIdOptions = (items, { valueKey, labelKey }) => {
  if (!Array.isArray(items)) return []
  return items
    .filter((item) => item && typeof item === 'object')
    .map((item) => ({
      id: resolveOptionValue(item, valueKey),
      label: resolveOptionLabel(item, labelKey),
    }))
    .filter((option) => option.id !== null)
    .map((option) => ({
      id: option.id,
      label: option.label || String(option.id),
    }))
}

const resolveOptionValue = (item, legacyKey) => {
  const candidates = [item.value, item[legacyKey]]
  for (const candidate of candidates) {
    if (candidate !== undefined && candidate !== null) return candidate
  }
  return null
}

const resolveOptionLabel = (item, legacyKey) => {
  const candidates = [item.label, item[legacyKey]]
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.length > 0) return candidate
  }
  return null
}

export const buildOptionsFieldLabelMap = (optionsFields, fieldKey) => {
  const map = {}
  if (!Array.isArray(optionsFields)) return map
  const field = optionsFields.find((item) => item && item.key === fieldKey)
  if (!field || !Array.isArray(field.items)) return map
  for (const item of field.items) {
    if (!item || item.id === undefined || item.id === null) continue
    if (typeof item.label !== 'string' || item.label.length === 0) continue
    map[String(item.id)] = item.label
  }
  return map
}

export const filterOptionsFieldsForProfile = (
  optionsFields,
  { isClient } = {}
) => {
  if (!Array.isArray(optionsFields)) return []
  if (!isClient) return optionsFields
  return optionsFields.filter(
    (field) => !(field && CONSULTANT_ONLY_FIELD_KEYS.has(field.key))
  )
}
